import s from './post-payment.module.scss'
import { IconBolt, IconCalendar, IconChevronRight, IconCircleCheckFilled, IconClock, IconTicket } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import classNames from 'classnames';
import { Button } from '@shared/ui/button/button';
import { queryClient } from '@shared/api/query-client';
import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { ProgressBar } from '@shared/ui/progress-bar/progress-bar';
import { getCDNPath } from '@utils/lib/getCDNpath';

const Chip = ({ icon, children }) => (
	<span className={s.chip}>
		{icon}
		{children}
	</span>
)

export const PostPayment = ({appointment, isAppointment, order}) => {
	const teacher = appointment?.teacher
	const {data: profile} = queryClient.getQueryData(['profile'])

	// SVG-маскот рисует чёрный паттерн, пока встроенный PNG декодируется —
	// показываем картинку только после полной загрузки
	const [isMascotLoaded, setIsMascotLoaded] = useState(false)

	const pack = order?.items?.find((item) => item?.service_type === 'appointments_pack')?.pack || null

	const hasStart = !!appointment?.starts_at
	const date = hasStart ? new TZDate(appointment.starts_at, profile?.timezone) : null
	const time = hasStart ? format(date, 'HH:mm') : '—'
	const dateString = hasStart ? format(date, 'd MMMM yyyy', { locale: ru }) : '—'
	const packExpiresAt = pack?.expires_at ? format(new TZDate(pack.expires_at, profile?.timezone), 'd MMMM yyyy', { locale: ru }) : '—'

	const title = isAppointment ? 'Занятие забронировано!' : pack ? 'Пакет занятий оплачен!' : 'Успешная оплата!'
	const subtitle = isAppointment
		? 'Занятие появилось в вашем расписании. Мы пришлём напоминание перед его началом.'
		: pack
			? `В пакете ${pack.lessons_count} занятий - их можно использовать на странице бронирования преподавателя, оплата больше не потребуется.`
			: 'Платёж прошёл успешно. Все детали - ниже.'

	const infoItems = pack ? [
		{ label: 'Преподаватель', value: order?.recipient_name },
		{ label: 'Занятий в пакете', value: pack.lessons_count },
		{ label: 'Срок действия до', value: packExpiresAt },
		{ label: 'Стоимость пакета', value: `${order?.amount ? order.amount / 100 : '—'} ₽` },
	] : [
		{ label: 'Дата', value: dateString },
		{ label: 'Время начала занятия', value: time },
		{ label: 'Продолжительность занятия', value: '50 мин.' },
		{ label: 'Инструмент', value: appointment?.instrument?.name, withEmoji: true },
		{ label: 'Преподаватель', value: isAppointment ? `${teacher?.name || ''} ${teacher?.lname || ''}`.trim() : order?.recipient_name },
		...(!isAppointment ? [{ label: 'Стоимость занятия', value: `${order?.amount ? order.amount / 100 : '—'} ₽` }] : []),
	]

	return (
		<motion.div
			className={s.wrapper}
			initial={{ opacity: 0, y: 16 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
		>
			<div className={s.card}>
				<div className={s.header}>
					<div className={s.headerContent}>
						<motion.span
							className={s.badge}
							initial={{ scale: 0 }}
							animate={{ scale: 1 }}
							transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.1 }}
						>
							<IconCircleCheckFilled size={36} className={s.badgeIcon}/>
						</motion.span>
						<h2 className={s.title}>{title}</h2>
						<p className={s.subtitle}>{subtitle}</p>
						<div className={s.chips}>
							{pack ? (
								<>
									<Chip icon={<IconTicket size={18}/>}>{pack.lessons_count} занятий</Chip>
									<Chip icon={<IconCalendar size={18}/>}>действует до {packExpiresAt}</Chip>
								</>
							) : (
								<>
									{appointment?.instrument?.name ? <Chip icon={<IconBolt size={18}/>}><TextWithEmoji text={appointment?.instrument?.name} className={s.chipEmoji}/></Chip> : null}
									{hasStart ? <Chip icon={<IconCalendar size={18}/>}>{dateString}</Chip> : null}
									{hasStart ? <Chip icon={<IconClock size={18}/>}>в {time}</Chip> : null}
								</>
							)}
						</div>
					</div>
					<img
						className={classNames(s.mascot, isMascotLoaded && s.mascotLoaded)}
						src={getCDNPath('images/mascot/man-thumbs-up.webp')}
						alt=""
						onLoad={() => setIsMascotLoaded(true)}
						onError={(e) => { e.target.style.display = 'none'; }}
					/>
				</div>

				<div className={s.info}>
					{infoItems.map(({label, value, withEmoji}) => (
						<div className={s.infoItem} key={label}>
							<div className={s.infoLabel}>{label}</div>
							<div className={s.infoValue}>
								{withEmoji ? <TextWithEmoji text={value}/> : (value ?? '—')}
							</div>
						</div>
					))}
					{pack ? (
						<div className={s.packProgress}>
							<div className={s.packProgressHeader}>
								<span className={s.infoLabel}>Использовано занятий</span>
								<span className={s.packProgressValue}>{pack.lessons_used || 0} из {pack.lessons_count}</span>
							</div>
							<ProgressBar currentStep={pack.lessons_used || 0} totalSteps={pack.lessons_count}/>
						</div>
					) : null}
				</div>

				<div className={s.buttons}>
					{isAppointment ? (
						<>
							<Button theme='secondary' label='На главную' to={'/'} className={s.btn}/>
							<Button label='Перейти к занятию' to={`/appointment/${appointment?.id}`} rightIcon={<IconChevronRight size={16}/>} className={s.btn}/>
						</>
					) : pack ? (
						<>
							<Button theme='secondary' label='На главную' to={'/'} className={s.btn}/>
							<Button disabled={!pack?.teacher?.teacher_slug} label='Записаться' to={`/teacher/booking/${pack?.teacher?.teacher_slug}`} className={s.btn}/>
						</>
					) : (
						<>
							<Button theme='secondary' label='На главную' to={'/'} className={s.btn}/>
							<Button disabled={!appointment?.teacher?.teacher_slug} label='Записаться' to={`/teacher/booking/${appointment?.teacher?.teacher_slug}`} className={s.btn}/>
						</>
					)}
				</div>

				{order?.id ? <span className={s.orderNumber}>Заказ №{order.id}</span> : null}
			</div>
		</motion.div>
	)
}
